import { z } from 'zod'

import type { ReplenParams } from '@/hooks/queries/replen'
import { codeList } from '@/lib/codes'

/**
 * The view state the Replen page reads from the URL.
 *
 * The warehouse is the one required choice: a SKU's class is per warehouse.
 * The page and its size are view state too; the server pages. Unsaved edits
 * are not view state and stay out of the link.
 */
const text = z.string().optional().catch(undefined)

/** Rows per page the picker offers; the API serves at most 100. */
export const REPLEN_PAGE_SIZES = [25, 50, 100] as const
export const REPLEN_DEFAULT_PAGE_SIZE = 50

export const replenSearchSchema = z.object({
  warehouse: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The warehouse whose replen classes are shown; nothing loads without it.'),
  skus: text.describe('SKU codes to show, comma-separated, exact.'),
  q: text.describe('Text matched against the SKU code or name.'),
  brand: text.describe('The brand name to narrow to.'),
  category: text.describe('The category name to narrow to.'),
  retailerSkus: text.describe('Retailer SKUs to show, comma-separated, exact.'),
  kind: z
    .enum(['R', 'N', 'T', 'NONE'])
    .optional()
    .catch(undefined)
    .describe('The replen class to narrow to: R, N, T, or NONE for not set.'),
  focus: z
    .enum(['yes', 'no'])
    .optional()
    .catch(undefined)
    .describe('Only focus SKUs (yes) or only non-focus SKUs (no).'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the list to show, from 1.'),
  size: z
    .number()
    .int()
    .refine((size) => REPLEN_PAGE_SIZES.some((option) => option === size))
    .optional()
    .catch(undefined)
    .describe('Rows per page: 25, 50 or 100.'),
})

export type ReplenSearch = z.infer<typeof replenSearchSchema>

/** Drop anything empty or at its default, so a URL carries only what was chosen. */
export function withoutReplenDefaults(search: ReplenSearch): ReplenSearch {
  return Object.fromEntries(
    Object.entries(search).filter(
      ([key, value]) =>
        value !== undefined &&
        value !== '' &&
        !(key === 'page' && value === 1) &&
        !(key === 'size' && value === REPLEN_DEFAULT_PAGE_SIZE),
    ),
  )
}

/** What the endpoint is asked for, or `undefined` while no warehouse is chosen. */
export function toReplenParams(search: ReplenSearch): ReplenParams | undefined {
  if (search.warehouse === undefined) return undefined
  return {
    warehouseId: search.warehouse,
    skuCodes: codeList(search.skus),
    q: search.q || undefined,
    brand: search.brand || undefined,
    category: search.category || undefined,
    retailerSkus: codeList(search.retailerSkus),
    kind: search.kind,
    focus: search.focus === undefined ? undefined : search.focus === 'yes',
    page: search.page ?? 1,
    limit: search.size ?? REPLEN_DEFAULT_PAGE_SIZE,
  }
}
